/**
 * Auth-State via Zustand.
 *
 * Access-Token im Memory (NICHT in localStorage — XSS-Schutz).
 * Refresh-Token läuft als HttpOnly-Cookie über den Browser.
 * Active-Tenant-ID + Active-Scan-Environment-ID werden in sessionStorage
 * gespiegelt, damit ein Reload den aktiven Kontext nicht vergisst.
 */
import { create } from "zustand";
import type { AuthMe, TenantMembership } from "@/types/api";

const TENANT_KEY = "scantax.activeTenantId";
const SCAN_ENV_KEY = "scantax.activeScanEnvironmentId";

interface AuthState {
  accessToken: string | null;
  user: AuthMe | null;
  activeTenantId: number | null;
  activeScanEnvironmentId: number | null;

  setSession: (accessToken: string, user: AuthMe) => void;
  setUser: (user: AuthMe) => void;
  setActiveTenant: (tenantId: number) => void;
  setActiveScanEnvironment: (envId: number | null) => void;
  clear: () => void;

  activeTenant: () => TenantMembership | null;
}

function loadInitialTenantId(): number | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(TENANT_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function loadInitialScanEnvId(): number | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(SCAN_ENV_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  activeTenantId: loadInitialTenantId(),
  activeScanEnvironmentId: loadInitialScanEnvId(),

  setSession: (accessToken, user) => {
    const prev = get().activeTenantId;
    const stillValid =
      prev != null && user.tenants.some((t) => t.tenant_id === prev);
    const tenantId = stillValid
      ? prev!
      : user.tenants[0]?.tenant_id ?? null;
    if (tenantId != null) sessionStorage.setItem(TENANT_KEY, String(tenantId));
    set({ accessToken, user, activeTenantId: tenantId });
  },

  setUser: (user) => set({ user }),

  setActiveTenant: (tenantId) => {
    sessionStorage.setItem(TENANT_KEY, String(tenantId));
    // Bei Tenant-Wechsel: Scan-Environment zurücksetzen (Default des neuen Tenants übernimmt)
    sessionStorage.removeItem(SCAN_ENV_KEY);
    set({ activeTenantId: tenantId, activeScanEnvironmentId: null });
  },

  setActiveScanEnvironment: (envId) => {
    if (envId == null) sessionStorage.removeItem(SCAN_ENV_KEY);
    else sessionStorage.setItem(SCAN_ENV_KEY, String(envId));
    set({ activeScanEnvironmentId: envId });
  },

  clear: () => {
    sessionStorage.removeItem(TENANT_KEY);
    sessionStorage.removeItem(SCAN_ENV_KEY);
    set({
      accessToken: null,
      user: null,
      activeTenantId: null,
      activeScanEnvironmentId: null,
    });
  },

  activeTenant: () => {
    const { user, activeTenantId } = get();
    if (!user || activeTenantId == null) return null;
    return user.tenants.find((t) => t.tenant_id === activeTenantId) ?? null;
  },
}));
